import { useState } from "react";
import { Camera, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useBlobUrl } from "@/hooks/use-assets";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";

export const CATS = { informatique: "Informatique", machine: "Machine / outillage", vehicule: "Véhicule", mobilier: "Mobilier", telephonie: "Téléphonie", electromenager: "Électroménager", autre: "Autre" };
const BASE = ["name", "brand", "model", "serial_number", "location", "supplier", "purchase_date", "purchase_price", "warranty_end", "maintenance_every_months", "next_maintenance", "notes", "photo_id"];
const R = ["name", "phone", "email", "address"];
const init = (e) => ({ category: e?.category || "autre", ...Object.fromEntries(BASE.map((k) => [k, e?.[k] ?? ""])), ...Object.fromEntries(R.map((k) => [`r_${k}`, e?.repairer?.[k] ?? ""])) });

const F = ({ id, label, v, set, type = "text", ...p }) => (
  <div className="space-y-1"><Label htmlFor={`eq-${id}`} className="text-xs">{label}</Label>
    <Input id={`eq-${id}`} type={type} value={v[id]} onChange={(e) => set({ ...v, [id]: e.target.value })} data-testid={`eq-form-${id}`} {...p} /></div>
);

export const EquipmentPhoto = ({ cid, photoId, className = "" }) => {
  const url = useBlobUrl(photoId ? `/business/companies/${cid}/equipment/files/${photoId}` : null);
  return url ? <img src={url} alt="" className={`object-cover ${className}`} data-testid="eq-photo" /> : null;
};

const PhotoScan = ({ cid, v, setV }) => {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/business/companies/${cid}/equipment/photo`, fd);
      const s = data.suggestion;
      setV((cur) => ({ ...cur, photo_id: data.photo_id, ...(s ? { name: cur.name || s.name, category: cur.category === "autre" ? s.category : cur.category, brand: cur.brand || s.brand || "", model: cur.model || s.model || "", location: cur.location || s.location || "" } : {}) }));
      toast.success(s ? "Fiche pré-remplie depuis la photo, vérifiez les champs" : "Photo ajoutée (analyse indisponible)");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3 rounded-xl border border-dashed border-primary/40 bg-primary/5 p-3" data-testid="eq-photo-scan">
      {v.photo_id ? <EquipmentPhoto cid={cid} photoId={v.photo_id} className="h-16 w-16 rounded-lg" /> : <span className="flex h-16 w-16 items-center justify-center rounded-lg bg-primary/10 text-primary"><Sparkles className="h-6 w-6" /></span>}
      <div className="min-w-0 flex-1 text-sm"><b>Prendre en photo l'équipement</b><p className="text-xs text-muted-foreground">L'IA reconnaît l'objet, la marque et le modèle visibles et remplit la fiche.</p></div>
      <Button type="button" variant="outline" size="sm" className="relative rounded-full" disabled={busy} asChild>
        <label data-testid="eq-photo-btn">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Camera className="mr-1 h-4 w-4" />}{busy ? "Analyse…" : v.photo_id ? "Changer" : "Photo"}
          <input type="file" accept="image/*" capture="environment" className="sr-only" onChange={onFile} disabled={busy} data-testid="eq-photo-input" /></label>
      </Button>
    </div>
  );
};

export const EquipmentForm = ({ company, item, open, onOpenChange, onSaved }) => {
  const [v, setV] = useState(() => init(item));
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const n = (x) => (x === "" ? null : x);
    const body = { category: v.category, ...Object.fromEntries(BASE.map((k) => [k, n(v[k])])), repairer: Object.fromEntries(R.map((k) => [k, n(v[`r_${k}`])])) };
    body.purchase_price = body.purchase_price === null ? null : Number(body.purchase_price);
    body.maintenance_every_months = body.maintenance_every_months === null ? null : Number(body.maintenance_every_months);
    setBusy(true);
    try {
      const url = `/business/companies/${company.id}/equipment`;
      await (item ? api.put(`${url}/${item.id}`, body) : api.post(url, body));
      toast.success(item ? "Équipement mis à jour" : "Équipement ajouté");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const p = { v, set: setV };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="eq-form-dialog">
        <DialogHeader><DialogTitle>{item ? "Modifier l'équipement" : "Ajouter un équipement"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <PhotoScan cid={company.id} v={v} setV={setV} />
          <div className="grid gap-3 sm:grid-cols-2">
            <F id="name" label="Nom *" required minLength={2} placeholder="Ex. Imprimante bureau" {...p} />
            <div className="space-y-1"><Label className="text-xs">Catégorie</Label>
              <Select value={v.category} onValueChange={(c) => setV({ ...v, category: c })}><SelectTrigger data-testid="eq-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CATS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></div>
            <F id="brand" label="Marque" {...p} /><F id="model" label="Modèle" {...p} />
            <F id="serial_number" label="N° de série" {...p} /><F id="location" label="Emplacement / local" {...p} />
            <F id="supplier" label="Acheté chez" {...p} /><F id="purchase_date" label="Date d'achat" type="date" {...p} />
            <F id="purchase_price" label="Prix d'achat HT (€)" type="number" min="0" step="0.01" {...p} /><F id="warranty_end" label="Fin de garantie" type="date" {...p} />
          </div>
          <fieldset className="grid gap-3 rounded-xl border border-border p-3 sm:grid-cols-2"><legend className="px-1 text-sm font-bold">Entretien</legend>
            <F id="maintenance_every_months" label="Tous les (mois)" type="number" min="1" max="120" {...p} /><F id="next_maintenance" label="Prochain entretien" type="date" {...p} /></fieldset>
          <fieldset className="grid gap-3 rounded-xl border border-border p-3 sm:grid-cols-2"><legend className="px-1 text-sm font-bold">Réparateur / SAV</legend>
            <F id="r_name" label="Nom" {...p} /><F id="r_phone" label="Téléphone" type="tel" {...p} />
            <F id="r_email" label="E-mail" type="email" {...p} /><F id="r_address" label="Adresse" {...p} /></fieldset>
          <div className="space-y-1"><Label htmlFor="eq-notes" className="text-xs">Notes</Label><Textarea id="eq-notes" value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} maxLength={1000} data-testid="eq-form-notes" /></div>
          <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="eq-form-submit">{busy ? "Enregistrement…" : "Enregistrer"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
